import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  FEEDBACK_TYPE_LABELS,
  FEEDBACK_TYPES,
  FEEDBACK_URGENCIES,
  FEEDBACK_URGENCY_LABELS
} from '@malash-office/types';
import type { FeedbackType, FeedbackUrgency } from '@malash-office/types';
import { FeedbackDot } from '../FeedbackChip';
import { FEEDBACK_PILL_RADIUS, feedbackSurfaces, feedbackTypeTones, feedbackUrgencyDots } from '../feedbackTones';

const Label = ({ children }: { children: string }) => (
  <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700 }}>
    {children}
  </Box>
);

/** "סוג הפנייה" — five pills; the chosen one takes its type's colours and a ring. */
export const FeedbackTypePicker = ({
  value,
  onChange
}: {
  value: FeedbackType;
  onChange: (type: FeedbackType) => void;
}) => {
  const theme = useTheme();
  const tones = feedbackTypeTones(theme);
  const surfaces = feedbackSurfaces(theme);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Label>סוג הפנייה</Label>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {FEEDBACK_TYPES.map(type => {
          const active = type === value;
          return (
            <Box
              key={type}
              component="button"
              type="button"
              onClick={() => onChange(type)}
              sx={{
                height: 34,
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                px: 1.75,
                border: 'none',
                borderRadius: FEEDBACK_PILL_RADIUS,
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
                bgcolor: active ? tones[type].bg : surfaces.well,
                color: active ? tones[type].ink : 'text.secondary',
                boxShadow: active ? `inset 0 0 0 1.5px ${tones[type].ink}` : 'none'
              }}
            >
              <FeedbackDot color={tones[type].ink} />
              {FEEDBACK_TYPE_LABELS[type]}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/** "רמת דחיפות" — a three-way segmented control on a grey well. */
export const FeedbackUrgencyPicker = ({
  value,
  onChange
}: {
  value: FeedbackUrgency;
  onChange: (urgency: FeedbackUrgency) => void;
}) => {
  const theme = useTheme();
  const dots = feedbackUrgencyDots(theme);
  const surfaces = feedbackSurfaces(theme);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Label>רמת דחיפות</Label>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          bgcolor: surfaces.well,
          borderRadius: FEEDBACK_PILL_RADIUS,
          p: '3px',
          alignSelf: 'flex-start'
        }}
      >
        {FEEDBACK_URGENCIES.map(urgency => {
          const active = urgency === value;
          return (
            <Box
              key={urgency}
              component="button"
              type="button"
              onClick={() => onChange(urgency)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                px: 2,
                py: 0.875,
                border: 'none',
                borderRadius: FEEDBACK_PILL_RADIUS,
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                bgcolor: active ? 'common.white' : 'transparent',
                color: active ? 'text.primary' : 'text.secondary'
              }}
            >
              <FeedbackDot color={dots[urgency]} />
              {FEEDBACK_URGENCY_LABELS[urgency]}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
